import React, { useState, useEffect } from 'react';
import { Autocomplete, Avatar, Box, TextField, Typography, Paper } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import './modal.css';
import { firstLetterOf, userAvatarSx } from '../../utilities/userAvatar';

const SearchableDropdown = ({ workFlowModalTaskData, members, handleAssigneeUpdate, currentRowId, className }) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedValue, setSelectedValue] = useState(null);
    
    useEffect(() => {
        if (workFlowModalTaskData?.assignee?.length > 0) {
            setSelectedValue(workFlowModalTaskData?.assignee[0]);
        } else {
            setSelectedValue({ employeeName: 'Unassigned', avatarUrl: workFlowModalTaskData?.assignee?.employeeName });
        }
    }, [workFlowModalTaskData]);

    /* Clearing the field leaves selectedValue as [], so "no one picked" is the
       absence of a name rather than the literal "Unassigned" row. */
    const selectedName = Array.isArray(selectedValue)
        ? ''
        : selectedValue?.employeeName || selectedValue?.employeeEmail || '';

    const handleChange = (event, newValue) => {
        if (newValue?.employeeName === 'Unassigned') {
            setSelectedValue([]);
            newValue = "";
        } else {
            setSelectedValue(newValue);
        }

        if (currentRowId) {
            handleAssigneeUpdate(currentRowId, newValue);
        } else if (workFlowModalTaskData?._id) {
            handleAssigneeUpdate(workFlowModalTaskData?._id, newValue);
        } else {
            console.error("No valid ID available to update assignee.");
        }
    };

    return (
        <Autocomplete
            value={selectedValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            options={[{ employeeName: 'Unassigned' }, ...members]}
            getOptionLabel={(option) => option.employeeName||option?.employeeEmail || 'Unassigned'}
            disableClearable
            PaperComponent={(props) => (
                <Paper 
                    {...props} 
                    sx={{
                        maxHeight: '280px',
                        overflowY: 'auto',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                        border: '1px solid #e1e5e9',
                        marginTop: '4px',
                        '& .MuiAutocomplete-listbox': {
                            padding: '8px',
                            '& .MuiAutocomplete-option': {
                                borderRadius: '6px',
                                margin: '0',
                                padding: '12px',
                                minHeight: '48px',
                                transition: 'all 0.15s ease',
                                cursor: 'pointer',
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: '#f4f5f7',
                                },
                                '&[aria-selected="true"]': {
                                    backgroundColor: '#e3f2fd',
                                    '&:hover': {
                                        backgroundColor: '#e3f2fd',
                                    }
                                },
                                '&.Mui-focused': {
                                    backgroundColor: '#f4f5f7',
                                }
                            }
                        }
                    }}
                />
            )}
            renderInput={(params) => (
                <TextField
                    className={className}
                    {...params}
                    variant="outlined"
                    placeholder="Search assignees..."
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            fontSize: '12.5px',
                            '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#dfe1e6',
                                }
                            },
                            '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#0052cc',
                                    borderWidth: '2px'
                                }
                            }
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '12.5px',
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                                {!selectedName || selectedName === "Unassigned" ? (
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            backgroundColor: '#dfe1e6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <PersonIcon sx={{ fontSize: 14, color: '#626f86' }} />
                                    </Box>
                                ) : (
                                    <Avatar sx={userAvatarSx(24, selectedName)}>{firstLetterOf(selectedName)}</Avatar>
                                )}
                            </Box>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box 
                    component="li" 
                    {...props} 
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px !important',
                        margin: '0 !important',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        minHeight: '48px',
                        '&:hover': {
                            backgroundColor: '#f4f5f7 !important',
                        },
                        '&[aria-selected="true"]': {
                            backgroundColor: '#e3f2fd !important',
                        }
                    }}
                >
                    <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        marginRight: '12px', 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        {option.employeeName === "Unassigned" ? (
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    backgroundColor: '#dfe1e6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 18, color: '#626f86' }} />
                            </Box>
                        ) : (
                            <Avatar sx={userAvatarSx(32, option?.employeeName || option?.employeeEmail)}>
                                {firstLetterOf(option?.employeeName || option?.employeeEmail)}
                            </Avatar>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                            sx={{ 
                                fontSize: '12.5px',
                                fontWeight: option.employeeName === "Unassigned" ? '500' : '400',
                                color: '#172b4d',
                                lineHeight: 1.3,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {option?.employeeName || option?.employeeEmail || 'Unassigned'}
                        </Typography>
                        {option?.employeeEmail && option.employeeName !== "Unassigned" && (
                            <Typography 
                                sx={{ 
                                    fontSize: '11px',
                                    color: '#626f86',
                                    lineHeight: 1.2,
                                    mt: 0.25,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {option.employeeEmail}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            renderTags={(value, getTagProps) => (
                <Box className="d-flex align-items-center gap-2">
                    <Avatar src={value.avatarUrl} className="task_Work_avatar-small" />
                    <Typography sx={{ me: 1 }} className="task_Work_typography-small">
                        {value.employeeName || value.employeeEmail || 'Unassigned'}
                    </Typography>
                </Box>
            )}
        />
    );
};

export default SearchableDropdown;